import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../lib/queryKeys";
import { useAppSelector } from "../../store/hooks";
import { toAuditDateOfService } from "../../utils/pro-fee-audit";
import { useFhirClient } from "../useFhirClient";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const fetchEncounterDateOfService = async (
  client: { request: (url: string) => Promise<unknown> } | null,
  encounterId: string | null
): Promise<string | null> => {
  if (!client || !encounterId) return null;
  try {
    const encounter = await client.request(
      `Encounter/${encodeURIComponent(encounterId)}`
    );
    if (!isRecord(encounter) || !isRecord(encounter.period)) return null;
    return (
      toAuditDateOfService(
        typeof encounter.period.start === "string"
          ? encounter.period.start
          : null
      ) ||
      toAuditDateOfService(
        typeof encounter.period.end === "string" ? encounter.period.end : null
      ) ||
      null
    );
  } catch (error) {
    console.warn("Failed to load encounter date of service:", error);
    return null;
  }
};

export const useOpcdEncounterLookups = () => {
  const client = useFhirClient();
  const providerNpi = useAppSelector((state) => state.provider.providerNPI);
  const mrn = useAppSelector((state) => state.fhir.mrn);
  const encounterId = useAppSelector((state) => state.fhir.encounter);

  const dateQuery = useQuery({
    queryKey: queryKeys.opcdEncounterDateOfService(encounterId ?? ""),
    queryFn: () => fetchEncounterDateOfService(client, encounterId),
    enabled: Boolean(client && encounterId),
    staleTime: 60 * 1000,
    retry: 0,
  });

  return {
    providerNpi,
    mrn: mrn?.trim() || "",
    dateOfService: dateQuery.data || undefined,
  };
};
